import { colors, Spinner, Stack, Typography } from '@ui-components/neo.tax';
import React, { ReactElement } from 'react';

import { AscGroupingTicketOrCollection, RdGroupingTicketOrCollection } from '@neo.tax/shared/graphql';

import { EmptyStateInner, EmptyStateOuter, LoadingContainer, MainListContainer } from './ticket-grouping-list.styled';
import { GroupedTicketsComponentProps, MemoizedTicketGroupingItem } from './TicketGroupingItem';

type GroupingSelection = {
  isGroupingSelected: boolean;
  selectedTicketIds: Set<string>;
};

type TicketGroupingListProps = {
  GroupedTicketsComponent: React.ComponentType<GroupedTicketsComponentProps>;
  groupingTicketOrCollections: AscGroupingTicketOrCollection[] | RdGroupingTicketOrCollection[];
  groupingSelections: Record<string, GroupingSelection>;
  setGroupingSelections: React.Dispatch<React.SetStateAction<Record<string, GroupingSelection>>>;
  searchKeyword: string | null;
  loading: boolean;
  isMoveTicketsDisabled: boolean;
  // Whether the project drawer is a noise (Miscellaneous/Ops) project. Forwarded to each item to
  // prefix matching reasoning appropriately. Only set by ASC; RD omits it.
  isNoiseProject?: boolean;
  onTicketCountChange?: (groupingId: string, count: number) => void;
};

export const TicketGroupingList = ({
  GroupedTicketsComponent,
  groupingSelections,
  groupingTicketOrCollections,
  isMoveTicketsDisabled,
  isNoiseProject = false,
  loading,
  onTicketCountChange,
  searchKeyword,
  setGroupingSelections,
}: TicketGroupingListProps): ReactElement => {
  if (loading) {
    return (
      <LoadingContainer direction="column" alignItems="center" justifyContent="center" gap={2} height="100%">
        <Spinner size="sm" variant="primary" />
      </LoadingContainer>
    );
  }

  return (
    <MainListContainer direction="column" overflow="auto" height="100%">
      {groupingTicketOrCollections.length === 0 ? (
        <EmptyStateOuter direction="column">
          <EmptyStateInner direction="column" justifyContent="center">
            <Typography color={colors.gray[300]} fontSize="lg" fontWeight="semibold" textAlign="center">
              {searchKeyword ? 'No matching groups or collections' : 'No matching tickets'}
            </Typography>
            <Typography color={colors.gray[300]} fontSize="base" fontWeight="normal" textAlign="center">
              {searchKeyword ? 'Try a different search term.' : 'Try selecting a different time period or filters.'}
            </Typography>
          </EmptyStateInner>
        </EmptyStateOuter>
      ) : (
        <Stack direction="column">
          {groupingTicketOrCollections.map((groupingTicketOrCollection) => (
            <MemoizedTicketGroupingItem
              key={groupingTicketOrCollection.id}
              GroupedTicketsComponent={GroupedTicketsComponent}
              groupingTicketOrCollection={groupingTicketOrCollection}
              groupingSelections={groupingSelections}
              setGroupingSelections={setGroupingSelections}
              searchKeyword={searchKeyword}
              isMoveTicketsDisabled={isMoveTicketsDisabled}
              isNoiseProject={isNoiseProject}
              onTicketCountChange={onTicketCountChange}
            />
          ))}
        </Stack>
      )}
    </MainListContainer>
  );
};
